import { Document, Page, Text, View } from '@react-pdf/renderer'
import {
  clean,
  Footer,
  Header,
  INK,
  LINE,
  MUTED,
  NAVY,
  PAGE_STYLE,
  Pill,
  StatRow,
  TableHeader,
  TEAL,
  TINT,
} from './pdfKit'

// Generic branded list/register print-out: title band, optional stat strip,
// then one table that flows over as many pages as it needs (repeating
// header, zebra rows, page numbers). Shared by the fleet, VDI, safety check
// and staff availability Print buttons.
//
// columns: [{ label, flex, align }]
// rows: arrays of cells, one per column. A cell is a string/number, or
//   { pill: { label, color } } for a status chip, or
//   { lines: [main, sub] } for a bold line with a muted line beneath it.
const Cell = ({ cell, column, bold }) => {
  const style = {
    flex: column?.flex || 1,
    paddingHorizontal: 3,
    textAlign: column?.align || 'left',
  }
  if (cell && typeof cell === 'object' && cell.pill) {
    return (
      <View style={style}>
        <Pill label={cell.pill.label} color={cell.pill.color} />
      </View>
    )
  }
  if (cell && typeof cell === 'object' && cell.lines) {
    const [main, sub] = cell.lines
    return (
      <View style={style}>
        <Text style={{ fontSize: 7, color: INK, fontFamily: 'Helvetica-Bold' }}>
          {clean(main ?? '-')}
        </Text>
        {sub ? (
          <Text style={{ fontSize: 6.5, color: MUTED, marginTop: 1 }}>{clean(sub)}</Text>
        ) : null}
      </View>
    )
  }
  return (
    <Text
      style={{
        ...style,
        fontSize: 7,
        color: INK,
        fontFamily: bold ? 'Helvetica-Bold' : 'Helvetica',
      }}
    >
      {clean(cell ?? '-')}
    </Text>
  )
}

const ListReportPDF = ({
  title,
  subtitle,
  docLabel,
  orgName,
  logo,
  generatedBy,
  stats,
  columns,
  rows,
  emptyText = 'Nothing to print',
  orientation = 'landscape',
  boldFirst = true,
}) => {
  const generatedAt = new Date()
  return (
    <Document
      title={`${docLabel} - ${title}`}
      author={orgName}
      subject={docLabel}
      creator="AmbuNet"
      options={{ isEvalSupported: false }}
    >
      <Page size="A4" orientation={orientation} style={PAGE_STYLE}>
        <Header orgName={orgName} logo={logo} docLabel={docLabel} title={title} />

        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY }}>
            {clean(title)}
          </Text>
          <View style={{ height: 2, backgroundColor: TEAL, width: 40, marginTop: 5 }} />
          {subtitle ? (
            <Text style={{ fontSize: 7.5, color: MUTED, marginTop: 6 }}>{clean(subtitle)}</Text>
          ) : null}
        </View>

        {stats?.length ? <StatRow items={stats} /> : null}

        <TableHeader columns={columns} fixed />
        {rows.length === 0 ? (
          <Text style={{ fontSize: 7, color: MUTED, padding: 5 }}>{emptyText}</Text>
        ) : (
          rows.map((row, r) => (
            <View
              key={r}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 3.5,
                paddingHorizontal: 3,
                backgroundColor: r % 2 ? TINT : undefined,
                borderBottomWidth: 0.5,
                borderBottomColor: LINE,
              }}
              wrap={false}
            >
              {row.map((cell, c) => (
                <Cell key={c} cell={cell} column={columns[c]} bold={c === 0 && boldFirst} />
              ))}
            </View>
          ))
        )}

        <Footer
          orgName={orgName}
          docLabel={docLabel}
          title={title}
          generatedAt={generatedAt}
          generatedBy={generatedBy}
        />
      </Page>
    </Document>
  )
}

export default ListReportPDF
